import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  OPTOMETRIST_USER_TYPE_LABELS,
  type Optometrist,
  type OptometristUserType,
  type PracticeProfile,
} from '@joker/contracts'
import { useTRPC } from '../trpc'
import { Panel, Loading, ErrorNote } from '../ui'
import {
  COUNTRIES,
  countryFromName,
  displayPhone,
  e164,
  formatNational,
  parsePhone,
  toE164,
  type Country,
} from '../phone'

/**
 * One practice, on its own page.
 *
 * The commercial terms come first because they are the thing that actually gets
 * edited — the price a patient pays and our cut of it — and the totals on the
 * Optometrists table are computed straight from them.
 */
export function Practice({
  id,
  onBack,
  onOpenPerson,
}: {
  id: string
  onBack: () => void
  onOpenPerson: (id: string) => void
}) {
  const trpc = useTRPC()
  const list = useQuery(trpc.optometrists.list.queryOptions())

  if (list.error) return <ErrorNote error={list.error} />
  if (!list.data) return <Loading what="the practice" />

  const practice = list.data.find((o) => o.id === id)
  if (!practice) {
    return (
      <>
        <button className="back" onClick={onBack}>
          ← Optometrists
        </button>
        <p className="footnote">That practice is no longer listed.</p>
      </>
    )
  }

  return <PracticeEditor practice={practice} onDone={onBack} onOpenPerson={onOpenPerson} />
}

/* ------------------------------- the editor ------------------------------ */

function PracticeEditor({
  practice,
  onDone,
  onOpenPerson,
}: {
  practice: Optometrist
  onDone: () => void
  onOpenPerson: (id: string) => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [form, setForm] = useState<PracticeProfile>(() => toForm(practice))
  const [saved, setSaved] = useState(false)
  /* Opens read-only. Editing is a deliberate act, so a stray click on a page
     full of live commercial terms cannot change one. */
  const [editing, setEditing] = useState(false)

  const patients = useQuery(trpc.optometrists.patients.queryOptions({ practiceId: practice.id }))

  const save = useMutation(
    trpc.optometrists.update.mutationOptions({
      onSuccess: async () => {
        setSaved(true)
        setEditing(false)
        await qc.invalidateQueries()
      },
    }),
  )
  const setType = useMutation(
    trpc.optometrists.setUserType.mutationOptions({
      onSuccess: async () => {
        await qc.invalidateQueries()
      },
    }),
  )
  const setPractice = useMutation(
    trpc.optometrists.setPatientPractice.mutationOptions({
      onSuccess: async () => {
        setTerm('')
        await qc.invalidateQueries()
      },
    }),
  )
  /* The search runs only once there are two characters to search on, which is
     also what the route accepts — a blank box would otherwise ask the server
     for every patient who has no practice. */
  const [term, setTerm] = useState('')
  const candidates = useQuery({
    ...trpc.optometrists.searchPatients.queryOptions({
      practiceId: practice.id,
      term: term.trim(),
    }),
    enabled: term.trim().length >= 2,
  })

  /* Any edit clears the "saved" note, so it can never describe the field you are
     currently typing into. */
  useEffect(() => setSaved(false), [form])

  const set = <K extends keyof PracticeProfile>(k: K, v: PracticeProfile[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const isUS = (form.country ?? '').trim().toUpperCase() === 'US'

  /* Cancel puts back what is stored rather than what was typed — leaving a
     half-finished edit in the boxes would make the next Edit press start from
     something nobody saved. */
  const cancel = () => {
    setForm(toForm(practice))
    setEditing(false)
  }

  return (
    <>
      <button className="back" onClick={onDone}>
        ← Optometrists
      </button>
      <h2 className="view-title">{form.practiceName ?? practice.name}</h2>
      {(form.locationName ?? null) && <p className="branch">{form.locationName}</p>}
      <p className="view-sub">
        What a patient pays and what we keep, per patient. The Optometrists table multiplies our cut
        by the paying patients here, in this practice&rsquo;s own currency. Press <b>Edit</b> to
        change any of it.
      </p>

      <Panel title="Commercials" note={`${practice.paying} paying of ${practice.people} patients`}>
        {editing ? (
          <form
            className="pform"
            onSubmit={(e) => {
              e.preventDefault()
              save.mutate({ id: practice.id, patch: form })
            }}
          >
            <fieldset>
              <legend>Identity</legend>
              <Field
                label="Practice name"
                value={form.practiceName}
                onChange={(v) => set('practiceName', v)}
                placeholder={practice.name}
              />
              <Field
                label="Location / branch"
                value={form.locationName}
                onChange={(v) => set('locationName', v)}
                placeholder="East Cobb"
              />
              <Field
                label="Website"
                value={form.website}
                onChange={(v) => set('website', v)}
                placeholder="classicvisioncare.com"
              />
            </fieldset>

            <fieldset>
              <legend>Commercials</legend>
              <Field label="Currency" value={form.currency} onChange={(v) => set('currency', (v ?? '').toUpperCase())} placeholder="USD" />
              <Field
                label={`Patient pays, per patient (${form.currency})`}
                value={form.patientPriceCents == null ? null : String(form.patientPriceCents / 100)}
                onChange={(v) => set('patientPriceCents', toCents(v))}
                inputMode="decimal"
              />
              <Field
                label={`Our cut, per patient (${form.currency})`}
                value={form.ourCutCents == null ? null : String(form.ourCutCents / 100)}
                onChange={(v) => set('ourCutCents', toCents(v))}
                inputMode="decimal"
              />
            </fieldset>
            <fieldset>
              <legend>Where</legend>
              <Field label="Country" value={form.country} onChange={(v) => set('country', v)} placeholder="US, Switzerland" />
              <Field
                label="Address"
                value={form.address}
                onChange={(v) => set('address', v)}
                placeholder="1234 Johnson Ferry Rd"
              />
              <Field label="City" value={form.city} onChange={(v) => set('city', v)} />
              <Field
                label="ZIP / postal code"
                value={form.postalCode}
                onChange={(v) => set('postalCode', v)}
                placeholder="30068"
              />
              {/* State is a US-only idea, so it appears only once the country says US
                  rather than inviting a region for a Swiss practice. */}
              {isUS && <Field label="State" value={form.state} onChange={(v) => set('state', v)} placeholder="GA" />}
            </fieldset>

            <fieldset>
              <legend>Main optometrist</legend>
              <Field label="Name" value={form.optometristName} onChange={(v) => set('optometristName', v)} />
              <label className="ctl check">
                <input
                  type="checkbox"
                  checked={form.optometristIsDoctor}
                  onChange={(e) => set('optometristIsDoctor', e.target.checked)}
                />
                Is a Dr
              </label>
              <Field label="Email" type="email" value={form.optometristEmail} onChange={(v) => set('optometristEmail', v)} />
              <PhoneField
                label="Phone"
                value={form.optometristPhone}
                country={form.country}
                onChange={(v) => set('optometristPhone', v)}
              />
            </fieldset>

            <fieldset>
              <legend>Main contact</legend>
              <Field label="Name" value={form.contactName} onChange={(v) => set('contactName', v)} />
              <Field label="Email" type="email" value={form.contactEmail} onChange={(v) => set('contactEmail', v)} />
              <PhoneField
                label="Phone"
                value={form.contactPhone}
                country={form.country}
                onChange={(v) => set('contactPhone', v)}
              />
            </fieldset>


            <div className="pform-actions">
              <button type="submit" className="btn primary" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn" onClick={cancel}>
                Cancel
              </button>
              {saved && !save.isPending && <span className="muted">Saved.</span>}
              {save.error && <span className="bad-text">{save.error.message}</span>}
            </div>
          </form>
        ) : (
          <Summary practice={practice} form={form} onEdit={() => setEditing(true)} />
        )}
      </Panel>

      <Panel title="Patients" note="what each one is to this practice">
        {!patients.data ? (
          <Loading what="patients" />
        ) : (
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Email</th>
                  <th className="r">Oryx #</th>
                  <th className="r">Sessions</th>
                  <th className="r">Cortex</th>
                  <th>Type</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {patients.data.map((p) => (
                  <tr
                    key={p.id}
                    tabIndex={0}
                    onClick={() => onOpenPerson(p.id)}
                    onKeyDown={(e) => e.key === 'Enter' && onOpenPerson(p.id)}
                  >
                    <td>{p.name ?? <span className="muted">Unnamed</span>}</td>
                    <td>{p.email ?? <span className="muted">—</span>}</td>
                    <td className="r num">{p.oryxUserNumber ?? '—'}</td>
                    <td className="r num">{p.sessionsTotal}</td>
                    {/* The old dashboard's own count. Shown beside Joker's rather
                        than instead of it: where they disagree, the replica is
                        missing rows, and that gap is the thing worth seeing. */}
                    <td className="r num">
                      {p.reportedSessions == null ? (
                        <span className="muted">—</span>
                      ) : p.reportedSessions !== p.sessionsTotal ? (
                        <b className="warn-text">{p.reportedSessions}</b>
                      ) : (
                        p.reportedSessions
                      )}
                    </td>
                    {/* The dropdown sits inside a row that navigates, so its
                        clicks and key presses must not reach the row — otherwise
                        classifying somebody would open their record instead. */}
                    <td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <select
                        className="sel"
                        value={p.userType ?? ''}
                        disabled={setType.isPending}
                        onChange={(e) =>
                          setType.mutate({
                            personId: p.id,
                            type: (e.target.value || null) as OptometristUserType | null,
                          })
                        }
                      >
                        <option value="">Not classified</option>
                        {(Object.keys(OPTOMETRIST_USER_TYPE_LABELS) as OptometristUserType[]).map((t) => (
                          <option key={t} value={t}>
                            {OPTOMETRIST_USER_TYPE_LABELS[t]}
                          </option>
                        ))}
                      </select>
                    </td>
                    {/* Same guard as the dropdown: inside a row that navigates,
                        so the click must not also open the patient. */}
                    <td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="linkish"
                        disabled={setPractice.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Take ${p.name ?? 'this patient'} off ${form.practiceName ?? practice.name}? ` +
                                'They stay a patient — they just stop counting towards this practice.',
                            )
                          ) {
                            setPractice.mutate({ personId: p.id, practiceId: null })
                          }
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="roster-add">
          <label>
            <span>Add a patient</span>
            <input
              type="search"
              value={term}
              placeholder="Search by name or email"
              onChange={(e) => setTerm(e.target.value)}
            />
          </label>
          {term.trim().length >= 2 && (
            <div className="roster-hits">
              {!candidates.data ? (
                <p className="footnote">Searching…</p>
              ) : candidates.data.length === 0 ? (
                <p className="footnote">
                  No patient matches that, other than the ones already here.
                </p>
              ) : (
                <ul className="hits">
                  {candidates.data.map((c) => (
                    <li key={c.id}>
                      <span>
                        <strong>{c.name}</strong>
                        {c.email && <span className="muted"> · {c.email}</span>}
                        <span className="muted"> · {c.sessionsTotal} sessions</span>
                        {/* Where they sit now, because most people worth adding
                            are already on a placeholder practice and moving one
                            off a real practice should never be a surprise. */}
                        {c.currentPractice && (
                          <span className="muted"> · now under {c.currentPractice}</span>
                        )}
                      </span>
                      <button
                        type="button"
                        className="linkish"
                        disabled={setPractice.isPending}
                        onClick={() => {
                          if (
                            !c.currentPractice ||
                            window.confirm(
                              `${c.name} is under ${c.currentPractice}. ` +
                                `Move them to ${form.practiceName ?? practice.name}?`,
                            )
                          ) {
                            setPractice.mutate({ personId: c.id, practiceId: practice.id })
                          }
                        }}
                      >
                        {c.currentPractice ? 'Move here' : 'Add'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <p className="footnote">
          <strong>Cortex</strong> is the session count the old dashboard reports, highlighted
          where it disagrees with Joker&rsquo;s — which means the replica subset is missing those
          rows, not that the patient stopped training. A <strong>store demo</strong> is the account a practice uses to show the product on the
          shop floor, and a <strong>team member</strong> tried it themselves. Neither is hidden the
          way a test account is — they stay in every count, and this is what tells them apart from a
          paying patient.
        </p>
        <p className="footnote">
          Adding or removing a patient here sticks: the Oryx import rewrites every patient&rsquo;s
          practice on each run from the dashboard&rsquo;s own optometrist column, and a hand-made
          change is marked so that run leaves it alone. Removing does not delete anybody — they stay
          a patient, without a practice.
        </p>
      </Panel>
    </>
  )
}

/**
 * A country and a national number, side by side.
 *
 * The number is formatted as it is typed rather than only on save, so the field
 * always shows what the phone would show. What leaves here is E.164 — the flag
 * and the grouping are presentation, and storing them would make the number
 * undiallable from another country.
 */
function PhoneField({
  label,
  value,
  country,
  onChange,
}: {
  label: string
  value: string | null
  country: string | null
  onChange: (v: string | null) => void
}) {
  const fallback = countryFromName(country)
  const parsed = parsePhone(value, fallback)
  /* The picker is only steered by the practice's country while the field is
     empty; once a number exists, its own dial code wins — a US practice may well
     list a Swiss mobile. */
  const chosen: Country = parsed.national ? parsed.country : fallback

  const setCountry = (code: string) => {
    const next = COUNTRIES.find((c) => c.code === code) ?? fallback
    onChange(toE164(next, parsed.national))
  }

  return (
    <label className="pfield phone">
      <span>{label}</span>
      <div className="phone-row">
        <select
          className="sel flag"
          value={chosen.code}
          onChange={(e) => setCountry(e.target.value)}
          aria-label={`${label} country`}
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.flag} {c.dial}
            </option>
          ))}
        </select>
        <input
          type="tel"
          inputMode="tel"
          value={formatNational(chosen, parsed.national)}
          placeholder={chosen.dial === '+1' ? '(555) 123-4567' : ''}
          onChange={(e) => onChange(toE164(chosen, e.target.value))}
        />
      </div>
    </label>
  )
}

/**
 * The practice as it stands, read-only.
 *
 * Shows the stored value formatted the way it is used elsewhere — money with its
 * currency, phones the way a phone shows them — and says "not set" rather than
 * leaving a blank, so a missing contact is visibly missing rather than ambiguous.
 */
function Summary({
  practice,
  form,
  onEdit,
}: {
  practice: Optometrist
  form: PracticeProfile
  onEdit: () => void
}) {
  const money = (cents: number | null) =>
    cents == null
      ? null
      : new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currencyDisplay: 'narrowSymbol',
          currency: form.currency || 'USD',
          maximumFractionDigits: 0,
        }).format(cents / 100)

  /* Addressed the way each country writes it: "Marietta, GA 30068" in the US,
     "8004 Zürich" in Switzerland. Getting this wrong is the kind of detail that
     makes a record look like it was typed by a machine. */
  const isUS = (form.country ?? '').trim().toUpperCase() === 'US'
  const locality = isUS
    ? [form.city, [form.state, form.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    : [form.postalCode, form.city].filter(Boolean).join(' ')
  const where = [form.address, locality, form.country].filter(Boolean).join(', ')

  const lead = form.optometristName
    ? `${form.optometristIsDoctor ? 'Dr. ' : ''}${form.optometristName}`
    : null

  return (
    <div className="summary-wrap">
      <dl className="summary">
        <Row k="Practice name" v={form.practiceName ?? practice.name} />
        <Row k="Location / branch" v={form.locationName} />
        <Row k="Website" v={form.website} href={webUrl(form.website)} copy={form.website} />
        <Row k="Patient pays" v={money(form.patientPriceCents)} />
        {/* The agreed rate, not money earned: what we take for each paying
            patient. Earned is the row below, which leaves refunds out. */}
        <Row k="Our cut per patient" v={money(form.ourCutCents)} />
        <Row
          k="Revenue (USD)"
          v={new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          }).format(practice.revenueUsdCents / 100)}
        />
        <Row k="Where" v={where} copy={where || null} />
        <Row k="Main optometrist" v={lead} />
        <Row k="Optometrist email" v={form.optometristEmail} copy={form.optometristEmail} />
        <Row
          k="Optometrist phone"
          v={displayPhone(form.optometristPhone, form.country)}
          copy={e164(form.optometristPhone, form.country)}
        />
        <Row k="Main contact" v={form.contactName} />
        <Row k="Contact email" v={form.contactEmail} copy={form.contactEmail} />
        <Row k="Contact phone" v={displayPhone(form.contactPhone, form.country)} copy={e164(form.contactPhone, form.country)} />
      </dl>
      <div className="pform-actions">
        <button type="button" className="btn primary" onClick={onEdit}>
          Edit
        </button>
      </div>
    </div>
  )
}

/**
 * One line of the summary. When `copy` is given the value becomes a button that
 * puts it on the clipboard — and for a phone that is the stored E.164
 * (`+16015721536`), not the `(601) 572-1536` on screen: what gets pasted into a
 * dialler or a CRM has to carry the country code.
 */
/** A bare domain is still a link; give it a scheme so the browser agrees. */
function webUrl(raw: string | null): string | null {
  const v = (raw ?? '').trim()
  if (!v) return null
  return /^https?:\/\//i.test(v) ? v : `https://${v}`
}

function Row({
  k,
  v,
  copy,
  href,
}: {
  k: string
  v: string | null | undefined
  copy?: string | null
  href?: string | null
}) {
  const [copied, setCopied] = useState(false)

  const doCopy = async () => {
    if (!copy) return
    try {
      await navigator.clipboard.writeText(copy)
    } catch {
      /* Clipboard access is refused outside a secure context and in some
         browsers without a user-gesture heuristic; the textarea route works
         everywhere and costs nothing to keep. */
      const el = document.createElement('textarea')
      el.value = copy
      el.style.position = 'fixed'
      el.style.opacity = '0'
      document.body.appendChild(el)
      el.select()
      try {
        document.execCommand('copy')
      } finally {
        document.body.removeChild(el)
      }
    }
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1400)
  }

  return (
    <>
      <dt>{k}</dt>
      <dd>
        {!v ? (
          <span className="muted">not set</span>
        ) : href ? (
          /* A link and a copy control side by side: one inside the other would
             nest two things you can click, and neither would be reliable. */
          <span className="linked">
            <a href={href} target="_blank" rel="noreferrer noopener">
              {v}
            </a>
            {copy && (
              <button type="button" className="copyable bare" onClick={doCopy} title={`Copy ${copy}`}>
                <span className={`copied${copied ? ' on' : ''}`} aria-live="polite">
                  {copied ? 'Copied' : 'Copy'}
                </span>
              </button>
            )}
          </span>
        ) : copy ? (
          <button type="button" className="copyable" onClick={doCopy} title={`Copy ${copy}`}>
            {v}
            <span className={`copied${copied ? ' on' : ''}`} aria-live="polite">
              {copied ? 'Copied' : 'Copy'}
            </span>
          </button>
        ) : (
          v
        )}
      </dd>
    </>
  )
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  inputMode,
}: {
  label: string
  value: string | null
  onChange: (v: string | null) => void
  type?: string
  placeholder?: string
  inputMode?: 'decimal'
}) {
  return (
    <label className="pfield">
      <span>{label}</span>
      <input
        type={type}
        value={value ?? ''}
        placeholder={placeholder ?? ''}
        {...(inputMode ? { inputMode } : {})}
        /* Empty means "not known", which is a different thing from an empty
           string — the column is nullable and should stay that way. */
        onChange={(e) => onChange(e.target.value.trim() === '' ? null : e.target.value)}
      />
    </label>
  )
}

const toCents = (v: string | null): number | null => {
  if (v == null || v.trim() === '') return null
  const n = Number(v.replace(/[^\d.-]/g, ''))
  return Number.isFinite(n) ? Math.round(n * 100) : null
}

function toForm(o: Optometrist): PracticeProfile {
  return {
    practiceName: o.practiceName,
    locationName: o.locationName,
    website: o.website,
    address: o.address,
    postalCode: o.postalCode,
    optometristName: o.optometristName,
    optometristIsDoctor: o.optometristIsDoctor,
    optometristEmail: o.optometristEmail,
    optometristPhone: o.optometristPhone,
    contactName: o.contactName,
    contactEmail: o.contactEmail,
    contactPhone: o.contactPhone,
    country: o.country,
    city: o.city,
    state: o.state,
    currency: o.currency,
    patientPriceCents: o.patientPriceCents,
    ourCutCents: o.ourCutCents,
  }
}
